// AuthForm.js - সম্পূর্ণ updated version
import React, { useState, useEffect } from 'react';
import { useUser } from '../context/UserContext';

const AuthForm = () => {
    const { login, signInWithGoogle, loading, error, setError, user } = useUser();
    const [formData, setFormData] = useState({
        email: '',
        password: ''
    });
    const [showForm, setShowForm] = useState(false);

    // যদি user already logged in থাকে, তাহলে form show করবে না
    useEffect(() => {
        if (user) {
            setShowForm(false);
        } else {
            // Small delay দিয়ে form show করবে যাতে auto sign-in না হয়
            const timer = setTimeout(() => {
                setShowForm(true);
            }, 1000);
            return () => clearTimeout(timer);
        }
    }, [user]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        try {
            const result = await login(formData.email, formData.password);
            if (!result.success) {
                setError(result.message);
            }
        } catch (error) {
            setError(error.message);
        }
    };

    const handleGoogleSignIn = async () => {
        setError('');
        const result = await signInWithGoogle();
        if (!result.success) {
            setError(result.message);
        }
    };

    const handleChange = (e) => {
        setFormData(prev => ({
            ...prev,
            [e.target.name]: e.target.value
        }));
    };

    // যদি user already logged in থাকে
    if (user) {
        return (
            <div className="min-vh-100 d-flex align-items-center justify-content-center bg-light">
                <div className="container">
                    <div className="row justify-content-center">
                        <div className="col-md-6 col-lg-5">
                            <div className="card shadow-lg border-0 text-center">
                                <div className="card-body p-5">
                                    <div className="mb-4">
                                        <i className="fas fa-check-circle fa-4x text-success mb-3"></i>
                                        <h2 className="card-title fw-bold">Already Signed In</h2>
                                    </div>
                                    <p className="card-text text-muted mb-4">
                                        You are already signed in as <strong>{user.email}</strong>
                                    </p>
                                    <div className="d-flex justify-content-center gap-3">
                                        <button
                                            onClick={() => window.location.href = '/admin'}
                                            className="btn btn-primary"
                                        >
                                            <i className="fas fa-tachometer-alt me-2"></i>
                                            Go to Dashboard
                                        </button>
                                        <button
                                            onClick={() => window.location.reload()}
                                            className="btn btn-outline-secondary"
                                        >
                                            <i className="fas fa-refresh me-2"></i>
                                            Refresh
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // যদি loading চলতে থাকে
    if (loading || !showForm) {
        return (
            <div className="min-vh-100 d-flex align-items-center justify-content-center bg-light">
                <div className="text-center">
                    <div className="spinner-border text-primary mb-3" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                    <p className="text-muted">Checking authentication...</p>
                </div>
            </div>
        );
    }

    // Main login form
    return (
        <div className="min-vh-100 d-flex align-items-center justify-content-center bg-light">
            <div className="container">
                <div className="row justify-content-center">
                    <div className="col-md-6 col-lg-5">
                        <div className="card shadow-lg border-0">
                            <div className="card-header bg-primary text-white text-center py-4">
                                <h2 className="mb-0">
                                    <i className="fas fa-cog me-2"></i>
                                    Admin Panel
                                </h2>
                                <p className="mb-0 mt-2 small opacity-75">
                                    Sign in to access admin features
                                </p>
                            </div>

                            <div className="card-body p-4">
                                {/* Error Messages */}
                                {error && (
                                    <div className="alert alert-danger alert-dismissible fade show" role="alert">
                                        <i className="fas fa-exclamation-triangle me-2"></i>
                                        {error}
                                        <button
                                            type="button"
                                            className="btn-close"
                                            onClick={() => setError('')}
                                        ></button>
                                    </div>
                                )}

                                {/* Google Sign-In Button */}
                                <button
                                    className="btn btn-outline-dark w-100 py-2 mb-3 d-flex align-items-center justify-content-center"
                                    onClick={handleGoogleSignIn}
                                    disabled={loading}
                                >
                                    <svg className="me-2" width="18" height="18" viewBox="0 0 24 24">
                                        <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                                        <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                                        <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                                        <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                                    </svg>
                                    Continue with Google
                                </button>

                                <div className="divider text-center text-muted my-3">
                                    or sign in with email
                                </div>

                                {/* Form */}
                                <form onSubmit={handleSubmit}>
                                    <div className="mb-3">
                                        <label className="form-label fw-semibold">Email address</label>
                                        <input
                                            type="email"
                                            name="email"
                                            value={formData.email}
                                            onChange={handleChange}
                                            className="form-control"
                                            placeholder="Enter your admin email"
                                            required
                                            disabled={loading}
                                        />
                                    </div>

                                    <div className="mb-4">
                                        <label className="form-label fw-semibold">Password</label>
                                        <input
                                            type="password"
                                            name="password"
                                            value={formData.password}
                                            onChange={handleChange}
                                            className="form-control"
                                            placeholder="Enter your password"
                                            required
                                            disabled={loading}
                                        />
                                    </div>

                                    <button
                                        type="submit"
                                        disabled={loading}
                                        className="btn btn-primary w-100 py-2 fw-semibold"
                                    >
                                        {loading ? (
                                            <>
                                                <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                                                Signing in...
                                            </>
                                        ) : (
                                            <>
                                                <i className="fas fa-sign-in-alt me-2"></i>
                                                Sign In
                                            </>
                                        )}
                                    </button>
                                </form>

                                <div className="text-center mt-4">
                                    <small className="text-muted">
                                        Don't have admin access? <br />
                                        Regular users should register through the main website.
                                    </small>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AuthForm;